import {
  toWorkflowContactConnectionPresentation,
  withSignatureRequestInvitations,
} from "@/lib/utils/connections/workflow-contact-connections";
import type {
  FirmSignatureRequestListItemV2,
  MatterSignatureRequestListItemV2,
} from "@/lib/data/requests/signature-requests/signature-request";
import type {
  Matter,
  MatterContactConnection,
  MatterSignatureRequest,
  MatterUserConnection,
} from "@/lib/types";
import type { ReportsSignatureRequestReport } from "@/lib/types/Reports";
import { formatUsersName } from "@/lib/utils/format";

/**
 * Display name for user signers on signature-request list cards and search.
 * Report rows supply a full `userName` split into first/last; joining non-empty
 * parts avoids a trailing space from the global {@link formatUsersName} when
 * last name is missing, without changing app-wide user formatting.
 */
export const displaySignatureRequestUserSignerName = (
  signer: Pick<MatterUserConnection, "firstName" | "lastName">,
): string => {
  const parts = [signer.firstName, signer.lastName].filter(
    (p): p is string => typeof p === "string" && p.trim().length > 0,
  );
  if (parts.length > 0) {
    return parts.join(" ");
  }
  return (formatUsersName(signer) ?? "").trim();
};

type MatterSignatureRequestListItem = MatterSignatureRequest & {
  matterSignatureRequestContactSigners: MatterContactConnection[];
  matterSignatureRequestUserSigners: MatterUserConnection[];
};

const MATTER_FILE_PREFIX = "MATTER_FILE::";

export const parseMatterFileIdFromDestination = (
  destination?: string,
): string | undefined => {
  if (!destination?.startsWith(MATTER_FILE_PREFIX)) {
    return undefined;
  }

  return destination.slice(MATTER_FILE_PREFIX.length) || undefined;
};

const mapReportContactConnections = (
  row: ReportsSignatureRequestReport,
): MatterContactConnection[] => {
  const connections = row.signatureRequestReportContactConnections ?? [];

  return connections.map((connection) => {
    const contactId =
      connection.signatureRequestReportContactConnectionContactId ?? "";

    const emails = (
      connection.signatureRequestReportContactConnectionContactEmail ?? []
    ).map((email) => ({
      contactId,
      address: email.address ?? "",
      description: email.description,
      id: email.id ?? "",
      isPrimary: email.isPrimary,
      label: email.label,
    }));

    const phones = (
      connection.signatureRequestReportContactConnectionContactPhone ?? []
    ).map((phone) => ({
      contactId,
      description: phone.description,
      extension: phone.extension,
      id: phone.id ?? "",
      isPrimary: phone.isPrimary,
      label: phone.label,
      number: phone.number ?? "",
    }));

    return {
      dateCreated: row.dateCreated ?? "",
      dateModified: row.dateModified ?? "",
      matterContactConnectionContactAddress: [],
      matterContactConnectionContactEmail: emails,
      matterContactConnectionContactFax: [],
      matterContactConnectionContactId: contactId,
      matterContactConnectionContactIndividualSubTypes:
        connection.signatureRequestReportContactConnectionContactIndividualSubTypes,
      matterContactConnectionContactName:
        connection.signatureRequestReportContactConnectionContactName ?? "",
      matterContactConnectionContactOrganizationSubTypes:
        connection.signatureRequestReportContactConnectionContactOrganizationSubTypes,
      matterContactConnectionContactPhone: phones,
      matterContactConnectionContactType:
        connection.signatureRequestReportContactConnectionContactType ??
        "individual",
      matterContactConnectionContactTagConnection: [],
      matterContactConnectionId:
        connection.signatureRequestReportContactConnectionId ?? "",
      label: connection.signatureRequestReportContactConnectionLabel ?? "",
      matterContactConnectionSignatureRequestComplete:
        connection.signatureRequestReportContactConnectionSignatureRequestComplete,
      matterContactConnectionSignatureRequestPasscode:
        connection.signatureRequestReportContactConnectionSignatureRequestPasscode ??
        "",
      signatureRequestComplete:
        connection.signatureRequestReportContactConnectionSignatureRequestComplete ??
        false,
      signatureRequestPasscode:
        connection.signatureRequestReportContactConnectionSignatureRequestPasscode ??
        "",
      contact: {
        id: contactId,
        type:
          connection.signatureRequestReportContactConnectionContactType ??
          "individual",
        individualFirstName:
          connection.signatureRequestReportContactConnectionContactName ?? "",
        individualLastName: "",
        individualNickname: null,
        organizationName: null,
        groupName: null,
        emails,
        phones,
      },
      matterId: row.signatureRequestReportMatterId ?? "",
      matterSignatureRequestId: row.signatureRequestReportSignatureRequestId,
      matterSignatureRequestSignerOrder:
        connection.signatureRequestReportSignatureRequestSignerOrder,
    } as unknown as MatterContactConnection;
  });
};

const mapReportUserConnections = (
  row: ReportsSignatureRequestReport,
): MatterUserConnection[] => {
  const users = row.signatureRequestReportUserConnections ?? [];
  const matterId = row.signatureRequestReportMatterId ?? "";
  const signatureRequestId = row.signatureRequestReportSignatureRequestId ?? "";

  return users.map((user) => {
    const extended = user as Record<string, unknown>;
    const apiName =
      user.signatureRequestReportUserConnectionUserName?.trim() ?? "";
    const extFirst =
      typeof extended.firstName === "string" ? extended.firstName : "";
    const extLast =
      typeof extended.lastName === "string" ? extended.lastName : "";

    let firstName = extFirst;
    let lastName = extLast;
    if (apiName) {
      const parts = apiName.split(/\s+/).filter(Boolean);
      firstName = parts[0] ?? "";
      lastName = parts.slice(1).join(" ");
    }

    return {
      dateCreated: row.dateCreated ?? "",
      dateModified: row.dateModified ?? "",
      matterId,
      email:
        user.signatureRequestReportUserConnectionUserEmail ??
        (typeof extended.email === "string" ? extended.email : ""),
      fileId: null,
      firmId: "",
      firstName,
      id: user.signatureRequestReportUserConnectionId ?? "",
      label: user.signatureRequestReportUserConnectionLabel ?? "",
      lastName,
      litigationDateId: null,
      medicalTreatmentBalanceConfirmationId: null,
      middleName: null,
      owner: Boolean(extended.owner),
      phoneNumber:
        user.signatureRequestReportUserConnectionUserPhone ??
        (typeof extended.phoneNumber === "string" ? extended.phoneNumber : ""),
      signatureRequestComplete:
        user.signatureRequestReportUserConnectionSignatureRequestComplete ??
        null,
      signatureRequestId,
      signatureRequestPasscode:
        user.signatureRequestReportUserConnectionSignatureRequestPasscode ?? "",
      matterSignatureRequestSignerOrder:
        user.signatureRequestReportSignatureRequestSignerOrder,
      statuteOfLimitationsId: null,
      status: null,
      taskId: null,
      userId:
        user.signatureRequestReportUserConnectionUserId ??
        (typeof extended.id === "string" ? extended.id : ""),
      username: null,
      vehicleId: null,
    };
  });
};

/**
 * Minimal matter shape for global signature request cards. Full matter is not
 * loaded; notes/tasks/files/event counts on the card are zero until the user
 * opens the matter.
 */
export const createMinimalMatterStubForSignatureRequestReportRow = (
  row: ReportsSignatureRequestReport,
): Matter => {
  const matterId = row.signatureRequestReportMatterId ?? "";
  return {
    matterId,
    matterTitle: row.signatureRequestReportMatterTitle ?? "",
    matterEventLog: [],
    matterFileReference: [],
    matterNote: [],
    matterSignatureRequest: [],
    matterTask: [],
  } as unknown as Matter;
};

/**
 * Builds the list item for the global signature requests view from report
 * data only (no retrieve-matter merge).
 */
export const getSignatureRequestReportRowDocumentDisplayName = (
  row: ReportsSignatureRequestReport,
): string => {
  const extended = row as Record<string, unknown>;
  const matterName =
    typeof extended.signatureRequestReportMatterFileName === "string"
      ? extended.signatureRequestReportMatterFileName
      : undefined;
  const firmName =
    typeof extended.signatureRequestReportFirmFileName === "string"
      ? extended.signatureRequestReportFirmFileName
      : undefined;

  return (
    (matterName ?? firmName ?? "Signature request").trim() ||
    "Signature request"
  );
};

export const mapSignatureRequestReportRowToListItem = (
  row: ReportsSignatureRequestReport,
): MatterSignatureRequestListItem | null => {
  const matterSignatureRequestId = row.signatureRequestReportSignatureRequestId;
  const matterId = row.signatureRequestReportMatterId ?? "";

  if (!matterSignatureRequestId) {
    return null;
  }

  const contactSigners = mapReportContactConnections(row);
  const userSigners = mapReportUserConnections(row);

  const fallbackName = getSignatureRequestReportRowDocumentDisplayName(row);

  const fileId =
    parseMatterFileIdFromDestination(
      row.signatureRequestReportSignatureRequestFileIdDestination,
    ) ?? "";

  const fallback = {
    dateCreated: row.dateCreated ?? "",
    dateModified: row.dateModified ?? "",
    matterContactConnections: [],
    matterFileId: fileId,
    matterFileName: fallbackName,
    matterFilePresignedUrlKey: "",
    matterId,
    matterSignatureRequestExpirationDate:
      row.signatureRequestReportSignatureRequestExpirationDate ?? "",
    matterSignatureRequestFileIdDestination:
      row.signatureRequestReportSignatureRequestFileIdDestination ?? "",
    matterSignatureRequestId: matterSignatureRequestId,
    matterUserConnections: [],
  } as unknown as MatterSignatureRequest;

  return {
    ...fallback,
    matterSignatureRequestContactSigners: contactSigners,
    matterSignatureRequestUserSigners: userSigners,
  } as MatterSignatureRequestListItem;
};

type SignatureRequestListItemV2 =
  | FirmSignatureRequestListItemV2
  | MatterSignatureRequestListItemV2;

const asRecord = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};

const asString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

const asBoolean = (value: unknown): boolean => value === true;

const mapV2ContactConnections = (
  request: SignatureRequestListItemV2,
): MatterContactConnection[] =>
  "contactConnections" in request
    ? mapReportContactConnections(
        mapFirmSignatureRequestV2ToReportRow(request),
      ).map((presentation, index) => ({
        ...presentation,
        id: request.contactConnections[index].id,
        contactId: request.contactConnections[index].contactId,
        dateCreated: request.contactConnections[index].dateCreated,
        dateModified: request.contactConnections[index].dateModified,
        pendingSigner: request.contactConnections[index].pendingSigner,
        signerOrder: request.contactConnections[index].signerOrder,
      }))
    : request.connections.map((connection) => ({
        ...toWorkflowContactConnectionPresentation({
          connection,
          label: "Signer",
          matterId: "matterId" in request ? request.matterId : "",
        }),
        signatureRequestPasscode:
          request.invitations?.find(
            (item) => item.connectionId === connection.id,
          )?.passcode ?? "",
      }));

const mapV2UserConnections = (
  request: SignatureRequestListItemV2,
): MatterUserConnection[] =>
  withSignatureRequestInvitations(
    request.userConnections ?? [],
    request.invitations,
  ).map((connection) => {
    const record = asRecord(connection);
    const user = asRecord(record.user);

    return {
      id: asString(record.id) ?? "",
      label: asString(record.label) ?? "",
      matterId:
        "matterId" in request && typeof request.matterId === "string"
          ? request.matterId
          : "",
      userId: asString(record.userId) ?? asString(user.id) ?? "",
      firstName: asString(record.firstName) ?? asString(user.firstName) ?? "",
      middleName: asString(record.middleName) ?? asString(user.middleName),
      lastName: asString(record.lastName) ?? asString(user.lastName) ?? "",
      email: asString(record.email) ?? asString(user.email) ?? "",
      phoneNumber: asString(record.phoneNumber) ?? "",
      signatureRequestComplete: asBoolean(record.signatureRequestComplete),
      signerOrder:
        typeof record.signerOrder === "number" ? record.signerOrder : undefined,
      pendingSigner: record.pendingSigner === true,
      signatureRequestPasscode: asString(record.signatureRequestPasscode) ?? "",
    } as unknown as MatterUserConnection;
  });

/**
 * Adapts the tenant-wide V2 list response to the existing signature request
 * card contract. The adapter is intentionally the only compatibility boundary;
 * Firm signers retain their domain IDs; Matter signers use owner connections.
 */
export const mapSignatureRequestV2ToListItem = (
  request: SignatureRequestListItemV2,
): MatterSignatureRequestListItem => {
  const matterId = "matterId" in request ? request.matterId : "";

  return {
    dateCreated: request.dateCreated,
    dateModified: request.dateModified,
    matterId,
    matterFileId: request.fileId ?? "",
    matterFileName: request.name ?? "Signature request",
    matterFilePresignedUrlKey: request.presignedUrlKey ?? "",
    matterSignatureRequestContactSigners: mapV2ContactConnections(request),
    matterSignatureRequestExpirationDate: request.expirationDate ?? "",
    matterSignatureRequestFileIdDestination: request.fileIdDestination ?? "",
    matterSignatureRequestId: request.id,
    matterSignatureRequestUserSigners: mapV2UserConnections(request),
    matterContactConnections: [],
    matterUserConnections: [],
  } as unknown as MatterSignatureRequestListItem;
};

export const createMinimalMatterStubForSignatureRequestV2 = (
  matterId: string,
): Matter =>
  ({
    id: matterId,
    matterId,
    matterTitle: "Matter",
    matterEventLog: [],
    matterFileReference: [],
    matterNote: [],
    matterSignatureRequest: [],
    matterTask: [],
  }) as unknown as Matter;

export const mapFirmSignatureRequestV2ToReportRow = (
  request: FirmSignatureRequestListItemV2,
): ReportsSignatureRequestReport =>
  ({
    dateCreated: request.dateCreated,
    dateModified: request.dateModified,
    signatureRequestReportFirmId: request.firmId,
    signatureRequestReportFirmFileId: request.fileId,
    signatureRequestReportFirmFileName: request.name ?? "Signature request",
    signatureRequestReportFirmFilePresignedUrlKey: request.presignedUrlKey,
    signatureRequestReportContactConnections: request.contactConnections.map(
      (connection) => ({
        signatureRequestReportContactConnectionContactEmail:
          connection.contactEmails.map((email) => ({
            address: email.emailAddress ?? "",
          })),
        signatureRequestReportContactConnectionContactId: connection.contactId,
        signatureRequestReportContactConnectionContactName:
          connection.contactName ?? "",
        signatureRequestReportContactConnectionContactPhone:
          connection.contactPhones.map((phone) => ({
            id: phone.id,
            number: phone.number ?? "",
          })),
        signatureRequestReportContactConnectionId: connection.id,
        signatureRequestReportContactConnectionLabel: connection.label,
        signatureRequestReportContactConnectionSignatureRequestComplete:
          connection.signatureRequestComplete,
        signatureRequestReportContactConnectionSignatureRequestPasscode:
          request.invitations?.find(
            (item) => item.connectionId === connection.id,
          )?.passcode ?? connection.signatureRequestPasscode,
        signatureRequestReportSignatureRequestSignerOrder:
          connection.signerOrder ?? undefined,
      }),
    ),
    signatureRequestReportSignatureRequestExpirationDate:
      request.expirationDate,
    signatureRequestReportSignatureRequestFileIdDestination:
      request.fileIdDestination ?? undefined,
    signatureRequestReportSignatureRequestId: request.id,
    signatureRequestReportUserConnections: request.userConnections.map(
      (connection) => ({
        signatureRequestReportUserConnectionId: connection.id,
        signatureRequestReportUserConnectionLabel: connection.label,
        signatureRequestReportUserConnectionSignatureRequestComplete:
          connection.signatureRequestComplete,
        signatureRequestReportUserConnectionSignatureRequestPasscode:
          request.invitations?.find(
            (item) => item.connectionId === connection.id,
          )?.passcode ?? "",
        signatureRequestReportSignatureRequestSignerOrder:
          connection.signerOrder ?? undefined,
        signatureRequestReportUserConnectionUserEmail:
          connection.user?.email ?? "",
        signatureRequestReportUserConnectionUserId: connection.userId,
        signatureRequestReportUserConnectionUserName: [
          connection.user?.firstName,
          connection.user?.lastName,
        ]
          .filter(Boolean)
          .join(" "),
      }),
    ),
  }) as ReportsSignatureRequestReport;
